/**
 * The last line between a render error and a blank white page.
 *
 * There was no boundary anywhere in this app, so ANY error thrown during render
 * unmounted the whole tree and left a white screen recoverable only by reload —
 * with nothing on screen, nothing in the funnel, and no way for the user to tell
 * us what they were doing.
 *
 * It was reachable by an ordinary user on an ordinary day: several API errors
 * carry `detail` as an OBJECT (`{error, message}`), a few components typed it as
 * a string and put it straight into state, and React throws "Objects are not
 * valid as a React child" when that state is rendered. An MFD pressing the
 * primary button on a client page hit exactly that.
 *
 * A boundary does not fix those bugs — `errorMessage()` below does — but it
 * turns the whole class from "the app is gone" into "this panel broke, here is
 * a retry".
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { track } from "@/lib/track";

interface Props {
  children: ReactNode;
  /** Shown instead of the default card, if the caller wants tighter framing. */
  fallback?: ReactNode;
  /** Names the area in the log line, e.g. "client-detail". */
  label?: string;
  /** Change this to clear a caught error automatically. Route boundaries pass
   *  the pathname, so navigating away from a broken screen recovers on its own
   *  instead of stranding the reader on the error card. */
  resetKey?: string;
  /** Hide the reload button when a parent already offers recovery. */
  hideReload?: boolean;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props) {
    // Recover on navigation. Without this a caught error persists across route
    // changes, so one broken screen locks the reader out of every other screen
    // reachable through this boundary.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[error-boundary${this.props.label ? `:${this.props.label}` : ""}]`,
                  error, info.componentStack);
    // F32. The console line only ever existed on the machine it happened on,
    // so a screen crashing for real users produced no signal anywhere. Report
    // the SHAPE of the failure and nothing else: the boundary's label and the
    // error's class name. Never the message (it can quote a fund name or an
    // amount from the data that broke it), never the stack, never props.
    try {
      track("panel_error", {
        surface: this.props.label || "unlabelled",
        reason: error?.name || "Error",
      });
    } catch {
      /* a boundary that throws while reporting is worse than a quiet one */
    }
  }

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div
        role="alert"
        style={{
          margin: 16, padding: 16, borderRadius: 12,
          border: "1px solid var(--line)", background: "var(--paper-1)",
          maxWidth: 560,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          Something went wrong on this screen
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-mid)", marginBottom: 12 }}>
          {/* "nothing was changed" is a claim we cannot make: a render can
              fail AFTER a save has already reached the server. State what is
              actually known — the screen failed — and let the user verify. */}
          This screen stopped working. If you were saving something, reload and
          check whether it went through before trying again.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Try again first: a scoped boundary can usually re-render the one
              broken subtree, which keeps everything else on the page — and any
              unsaved input outside it — intact. A full reload throws all of
              that away, so it is the fallback, not the first offer. */}
          <button
            className="btn btn-primary"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
          {!this.props.hideReload && (
            <button
              className="btn btn-ghost"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
          )}
        </div>
      </div>
    );
  }
}

/**
 * Coerce anything an API threw into a STRING safe to render.
 *
 * `ApiError.body` is the parsed JSON response, and several endpoints return
 * `detail` as an object — `require_research_entitlement` and the adviser quota
 * gate both raise `HTTPException(detail={"error": ..., "message": ...})`.
 * Components that typed it as `detail?: string` assigned the object into state
 * and rendered it, which is a render-time throw, not a caught error.
 *
 * Always put API errors through this before they reach state.
 */
export function errorMessage(e: unknown, fallback = "Something went wrong."): string {
  if (typeof e === "string") return e;
  const body = (e as { body?: unknown } | undefined)?.body;
  const detail = (body as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
    const err = (detail as { error?: unknown }).error;
    if (typeof err === "string" && err.trim()) return err;
  }
  const msg = (e as { message?: unknown } | undefined)?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}
